import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import useGetPersonal from "~/pages/StaffEmployeePersonal/hook/useStaffPersonal";
import { useGetPositionsOptions } from "~/pages/StaffEmployeePersonal/hook/useGetPositionsOptions";
import MultiSelect from "~/shared/ui/Fields/MultiSelect";
import Table from "~/components/Table";
import { TableV2 } from "~/shared/ui/TableV2";
import s from "./StaffEmployeePersonal.less";
import { CERTIFICATION_OPTIONS_TABLE } from "~/pages/StaffEmployeePersonal/options";
import { useHistory } from "react-router-dom";
import Back from "~/components/Icons/arrow-prev2";
import Button from "~/shared/ui/Fields/Button";
import DateBlockMoratorium from "./components/DateBlockMoratorium";
import { getStaffEmployeePersonalColumns } from "./model/tableAdapter";

const formatDisplayDate = (dateString, isCompletedDate = false) => {
  if (!dateString) return isCompletedDate ? "Авто" : "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return dateString.split("T")[0];
};

const StaffEmployeePersonal = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { UserId, currentId, dateBegin, name, link, positionNames } =
    useGetLocationData() as ReturnType<typeof useGetLocationData> & {
      positionNames?: string;
    };
  const setData = useSetLocationData();
  const queryClient = useQueryClient();
  const [selectExpectations, setSelectExpectations] = useState([]);
  const [dataMoratorium, setDataMoratorium] = useState();
  const { control, setValue, watch } = useForm({
    defaultValues: {
      filterExpectations: currentId
        ? currentId
            .split(",")
            .map((id) => ({ value: id.trim(), label: id.trim() }))
        : [],
    },
  });
  const formattedDateBegin = formatDate(dateBegin);
  const { data, refetch, isLoading } = useGetPersonal(formattedDateBegin);
  const {
    options: positionsOptions,
    loading: isLoadingPositions,
    onScrollBottom,
    searchText,
    ensureOptionsByIds,
  } = useGetPositionsOptions(formattedDateBegin);

  const filterPosition = useMemo(() => {
    return [...positionsOptions].sort((a, b) => {
      const codeA = parseInt(a.label, 10) || 0;
      const codeB = parseInt(b.label, 10) || 0;
      return codeA - codeB;
    });
  }, [positionsOptions]);
  const levelPriority = ["базовый", "стандартный", "сертификация"];
  const formattedCourses = useMemo(() => {
    return (
      data?.courses
        ?.map((course) => ({
          ...course,
          completedDate: formatDisplayDate(course.completedDate, true),
          certificateExpirationDate: formatDisplayDate(
            course.certificateExpirationDate
          ),
        }))
        .sort((a, b) => {
          const codeA = parseInt(a.levelName, 10) || 0;
          const codeB = parseInt(b.levelName, 10) || 0;
          return codeA - codeB;
        }) || []
    );
  }, [data]);

  const sortedData = formattedCourses?.sort((a, b) => {
    const aIndex = levelPriority.indexOf(a.levelName);
    const bIndex = levelPriority.indexOf(b.levelName);

    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }

    return Number(a.codeIS) - Number(b.codeIS);
  });

  const filterExpectations = watch("filterExpectations");
  useEffect(() => {
    if (!filterPosition) {
      return;
    }
    const uniqueOptions = filterPosition.filter(
      (item, index, self) =>
        index ===
        self.findIndex((el) => String(el.value) === String(item.value))
    );

    if (!currentId) {
      setSelectExpectations(uniqueOptions);
      return;
    }

    const ids = currentId.split(",").map((id) => id.trim());
    const namesFromUrl = positionNames ? positionNames.split(",") : [];

    const defaultValues = ids.map((id, index) => {
      const foundItem = uniqueOptions.find(
        (item) => String(item.value) === id
      );
      return {
        value: id,
        label: foundItem ? foundItem.label : namesFromUrl[index] || id,
      };
    });

    const unresolvedIds = ids.filter(
      (id, index) =>
        !uniqueOptions.some((item) => String(item.value) === id) &&
        !namesFromUrl[index]
    );
    if (unresolvedIds.length) {
      ensureOptionsByIds(unresolvedIds);
    }

    setSelectExpectations(
      uniqueOptions.filter((item) => !ids.some((id) => String(item.value) === id))
    );

    const currentValues = watch("filterExpectations");
    if (JSON.stringify(currentValues) !== JSON.stringify(defaultValues)) {
      setValue("filterExpectations", defaultValues);
    }
  }, [filterPosition, currentId, positionNames]);

  useEffect(() => {
    const selectedIds = filterExpectations?.map((item) => item.value).join(",");
    if (selectedIds !== currentId) {
      const selectedNames =
        filterExpectations?.map((item) => item.label).join(",") || "";
      setData("currentId", selectedIds || "", "positionNames", selectedNames);
    }
  }, [filterExpectations, currentId, setData]);

  useEffect(() => {
    queryClient.invalidateQueries("getDataStaffPersonal");
    refetch();
  }, [UserId, currentId, dateBegin, refetch, queryClient]);

  const redirectToUserProfilePersonal = useCallback(() => {
    if (link) {
      history.push(link);
    } else {
      // @ts-ignore
      if (history.length > 1) {
        // @ts-ignore
        history.goBack();
      } else {
        history.push("/staff/employee");
      }
    }
  }, [history, link]);

  const goBack = () => window.close();
  const onLink = () => {
    // history.push("/editor/cep/view/ptp/");
    window.open("/editor/cep/view/ptp/", "_blank");
  };

  const columns = getStaffEmployeePersonalColumns(
    t,
    CERTIFICATION_OPTIONS_TABLE
  );

  return (
    <div className={s.wrapper}>
      {/*<div className={s.btn} onClick={redirectToUserProfilePersonal}>*/}
      {/*  <Back /> Назад*/}
      {/*</div>*/}
      <div className={s.s_user_title}>
        <Button
          txtBtn={""}
          onlyIcon
          onClick={goBack}
          customClassName={s.iconBack}
        >
          <Back />
        </Button>{" "}
        Завершенные курсы: {name}
      </div>
      <div className={s.wrapperFormTimeBlock}>
        <form className={s.wrapperForm}>
          <label htmlFor="filterExpectations">Должность</label>
          <div className={s.customLabelClass}>
            <MultiSelect
              name="filterExpectations"
              label=""
              control={control}
              options={selectExpectations}
              onScrollBottom={onScrollBottom}
              getSearchText={searchText}
              loading={isLoadingPositions}
              customInput={{ height: 13, marginTop: -1 }}
              customLabelClass={s.customLabelClass}
            />
          </div>
        </form>

        <Button
          txtBtn={"Программа обучения"}
          small
          green
          customClassName={s.buttonHistory}
          onClick={onLink}
        />

        <DateBlockMoratorium positions={data?.employee?.positions} />
      </div>
      <TableV2
        key={`${UserId}_${currentId}`}
        columns={columns}
        dataSource={formattedCourses}
        loading={isLoading || isLoadingPositions}
        wrapperClassName={s.users_table}
        pagination={false}
      />
      {/* <Table
        key={`${UserId}_${currentId}`}
        options={CERTIFICATION_OPTIONS_TABLE}
        items={sortedData}
        loading={isLoading || isLoadingPositions}
        refetch={refetch}
        noClick
        type="cep"
        customClassName={s.users_table}
      /> */}
    </div>
  );
};

export default React.memo(StaffEmployeePersonal);
